import React, { useState, useMemo } from 'react';

function getShiftClass(shift) {
  const s = shift.toLowerCase();
  if (s.includes('pagi') || s.includes('shift 1')) return 'pagi';
  if (s.includes('malam') || s.includes('shift 3') || (s.includes('shift 2') && s.includes('12'))) return 'malam';
  if (s.includes('siang') || s.includes('shift 2')) return 'siang';
  return 'other';
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const days = ['MIN', 'SEN', 'SEL', 'RAB', 'KAM', 'JUM', 'SAB'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
}

function formatDateFull(dateStr) {
  const d = new Date(dateStr);
  const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
  const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function ResultTable({ data, onUpdateData, apiBase }) {
  const [filter, setFilter] = useState('ALL');
  const [selectedChip, setSelectedChip] = useState(null); // { date, shiftIndex, empName, empIndex }

  const shiftTypes = useMemo(() => {
    return [...new Set(data.map((r) => r.shift))];
  }, [data]);

  const filtered = useMemo(() => {
    if (filter === 'ALL') return data;
    return data.filter((r) => r.shift === filter);
  }, [data, filter]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((row) => {
      if (!g[row.date]) g[row.date] = [];
      g[row.date].push(row);
    });
    return g;
  }, [filtered]);

  const handleExportCSV = () => {
    const headers = ['Tanggal', 'Shift', 'Jam', 'Karyawan', 'Jumlah'];
    const rows = data.map((r) => [
      r.date,
      r.shift,
      r.hours,
      r.employees.join('; '),
      r.employee_count,
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scheduly-jadwal-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportICal = async () => {
    try {
      if (apiBase) {
        const res = await fetch(`${apiBase}/export/ical`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ table: data }),
        });
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `scheduly-jadwal-${Date.now()}.ics`;
          a.click();
          URL.revokeObjectURL(url);
          return;
        }
      }
    } catch (e) {
      console.warn('Backend iCal failed, fallback to client generator', e);
    }

    // Client-side iCal RFC 5545 fallback
    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Scheduly//Shift Schedule//EN',
      'CALSCALE:GREGORIAN',
      'X-WR-CALNAME:Jadwal Shift Scheduly',
    ];

    data.forEach((r, idx) => {
      try {
        const [sH, eH] = r.hours.split('-');
        const cDate = r.date.replace(/-/g, '');
        const dtstart = `${cDate}T${sH.replace(':', '')}00`;
        const dtend = `${cDate}T${eH.replace(':', '')}00`;
        const emps = r.employees.join(', ') || 'Kosong';

        lines.push(
          'BEGIN:VEVENT',
          `UID:scheduly-${r.date}-${idx}@scheduly.app`,
          `DTSTART:${dtstart}`,
          `DTEND:${dtend}`,
          `SUMMARY:${r.shift} — ${emps}`,
          `DESCRIPTION:Shift ${r.shift} (${r.hours})\\nStaff: ${emps}`,
          'END:VEVENT'
        );
      } catch (err) {
        // continue
      }
    });

    lines.push('END:VCALENDAR');
    const icsContent = lines.join('\r\n');
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scheduly-jadwal-${Date.now()}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    try {
      const ExcelJS = require('exceljs');
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Scheduly SA Engine';
      workbook.created = new Date();

      // Color Palette & Fills
      const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } }; // Dark Navy
      const subHeaderFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5597' } }; // Medium Blue
      const pagiFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } }; // Light Yellow
      const siangFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2EFDA' } }; // Light Green
      const malamFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } }; // Light Blue
      const offFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } }; // Light Gray

      const fontHeader = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      const fontTitle = { name: 'Arial', size: 14, bold: true, color: { argb: 'FF1F4E79' } };
      const fontBold = { name: 'Arial', size: 10, bold: true };
      const fontNormal = { name: 'Arial', size: 10 };
      const fontMuted = { name: 'Arial', size: 9, italic: true, color: { argb: 'FF595959' } };

      const thinBorder = {
        top: { style: 'thin', color: { argb: 'FFD9D9D9' } },
        left: { style: 'thin', color: { argb: 'FFD9D9D9' } },
        bottom: { style: 'thin', color: { argb: 'FFD9D9D9' } },
        right: { style: 'thin', color: { argb: 'FFD9D9D9' } },
      };

      // Helper function for Shift Fill
      const getShiftFill = (shiftName) => {
        const s = (shiftName || '').toLowerCase();
        if (s.includes('pagi') || s.includes('shift 1')) return pagiFill;
        if (s.includes('siang') || s.includes('shift 2')) return siangFill;
        if (s.includes('malam') || s.includes('shift 3')) return malamFill;
        return offFill;
      };

      // -------------------------------------------------------------
      // SHEET 1: RINGKASAN JADWAL SHIFT
      // -------------------------------------------------------------
      const ws1 = workbook.addWorksheet('Ringkasan Jadwal');
      
      // Title
      const titleCell = ws1.getCell('A1');
      titleCell.value = 'SCHEDULY - JADWAL OPERATIONAL SHIFT';
      titleCell.font = fontTitle;

      const subTitleCell = ws1.getCell('A2');
      subTitleCell.value = `Tanggal Dibuat: ${new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`;
      subTitleCell.font = fontMuted;

      ws1.addRow([]); // Blank row

      // Table Headers
      const headers1 = ['Hari & Tanggal', 'Shift Work', 'Jam Kerja', 'Karyawan Bertugas', 'Jumlah Karyawan'];
      const headerRow1 = ws1.addRow(headers1);
      headerRow1.height = 24;
      headerRow1.eachCell((cell) => {
        cell.fill = headerFill;
        cell.font = fontHeader;
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = thinBorder;
      });

      data.forEach((r) => {
        const fullDateFormatted = formatDateFull(r.date);
        const empStr = (r.employees || []).join(', ');
        const row = ws1.addRow([fullDateFormatted, r.shift, r.hours, empStr, r.employee_count]);
        row.height = 20;

        const fill = getShiftFill(r.shift);
        row.eachCell((cell, colNumber) => {
          cell.font = fontNormal;
          cell.border = thinBorder;
          cell.alignment = { vertical: 'middle' };
          if (colNumber === 2) {
            cell.fill = fill;
            cell.font = fontBold;
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          } else if (colNumber === 5) {
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          }
        });
      });

      // Auto-fit columns
      ws1.columns = [
        { width: 26 },
        { width: 16 },
        { width: 18 },
        { width: 45 },
        { width: 18 },
      ];

      // -------------------------------------------------------------
      // SHEET 2: MATRIKS KARYAWAN (Tabel Per Nama & Hari)
      // -------------------------------------------------------------
      const ws2 = workbook.addWorksheet('Matriks Karyawan');
      
      const titleCell2 = ws2.getCell('A1');
      titleCell2.value = 'SCHEDULY - MATRIKS JADWAL INDIVIDUAL KARYAWAN';
      titleCell2.font = fontTitle;

      ws2.addRow([]); // Blank row

      const allDates = [...new Set(data.map((r) => r.date))].sort();
      const empSet = new Set();
      data.forEach((r) => (r.employees || []).forEach((e) => empSet.add(e)));
      const allEmps = [...empSet].sort();

      const headers2 = ['Nama Karyawan', ...allDates.map((d) => formatDate(d))];
      const headerRow2 = ws2.addRow(headers2);
      headerRow2.height = 24;
      headerRow2.eachCell((cell, colNum) => {
        cell.fill = colNum === 1 ? headerFill : subHeaderFill;
        cell.font = fontHeader;
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = thinBorder;
      });

      allEmps.forEach((emp) => {
        const rowVals = [emp];
        const shiftPerDate = [];

        allDates.forEach((d) => {
          const shifts = data
            .filter((r) => r.date === d && (r.employees || []).includes(emp))
            .map((r) => r.shift);
          const val = shifts.length > 0 ? shifts.join(', ') : 'OFF';
          rowVals.push(val);
          shiftPerDate.push(val);
        });

        const row = ws2.addRow(rowVals);
        row.height = 22;

        row.eachCell((cell, colNumber) => {
          cell.border = thinBorder;
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          if (colNumber === 1) {
            cell.font = fontBold;
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
          } else {
            const shiftVal = shiftPerDate[colNumber - 2];
            cell.fill = getShiftFill(shiftVal);
            if (shiftVal === 'OFF') {
              cell.font = fontMuted;
            } else {
              cell.font = fontBold;
            }
          }
        });
      });

      ws2.columns = [
        { width: 25 },
        ...allDates.map(() => ({ width: 15 })),
      ];

      // Export file buffer to browser download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scheduly-jadwal-shift-${Date.now()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export Excel failed:', e);
      alert('Gagal membuat file Excel: ' + e.message);
    }
  };

  const handleChipClick = (date, rowIndex, empName, empIndex) => {
    if (!onUpdateData) return;

    if (!selectedChip) {
      // Select source
      setSelectedChip({ date, rowIndex, empName, empIndex });
    } else {
      // Swap target
      if (selectedChip.date === date && selectedChip.rowIndex === rowIndex && selectedChip.empIndex === empIndex) {
        // Deselect if clicking same chip
        setSelectedChip(null);
        return;
      }

      // Execute Swap
      const newData = [...data];
      const sourceRowIndex = data.findIndex(
        (r) => r.date === selectedChip.date && r.shift === data[selectedChip.rowIndex]?.shift
      );
      const targetRowIndex = data.findIndex(
        (r) => r.date === date && r.shift === data[rowIndex]?.shift
      );

      if (sourceRowIndex !== -1 && targetRowIndex !== -1) {
        const srcEmps = [...newData[sourceRowIndex].employees];
        const tgtEmps = [...newData[targetRowIndex].employees];

        const temp = srcEmps[selectedChip.empIndex];
        srcEmps[selectedChip.empIndex] = tgtEmps[empIndex];
        tgtEmps[empIndex] = temp;

        newData[sourceRowIndex] = {
          ...newData[sourceRowIndex],
          employees: srcEmps,
        };
        newData[targetRowIndex] = {
          ...newData[targetRowIndex],
          employees: tgtEmps,
        };

        onUpdateData(newData);
      }

      setSelectedChip(null);
    }
  };

  return (
    <div className="result-section">
      <div className="result-controls">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="result-title">JADWAL SHIFT</span>
          {onUpdateData && (
            <span style={{ fontSize: 11, color: 'var(--yellow)', fontFamily: 'var(--font-mono)' }}>
              {selectedChip ? '⚡ KLIK PEGAWAI KEDUA UNTUK SWAP' : '💡 KLIK NAMA DUA PEGAWAI UNTUK TUKAR SHIFT'}
            </span>
          )}
        </div>

        <div className="filter-group">
          <button
            className={`filter-btn ${filter === 'ALL' ? 'active' : ''}`}
            onClick={() => setFilter('ALL')}
          >
            SEMUA
          </button>
          {shiftTypes.map((s) => (
            <button
              key={s}
              className={`filter-btn ${filter === s ? 'active' : ''}`}
              onClick={() => setFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-export" onClick={handleExportCSV} title="Export spreadsheet CSV">
            ↓ CSV
          </button>
          <button className="btn-export" onClick={handleExportICal} title="Import ke Google Calendar / Apple iCal">
            📅 iCAL
          </button>
          <button className="btn-export" onClick={handleExportExcel} title="Download file Excel multi-sheet (.xlsx) rapi">
            📊 EXCEL
          </button>
        </div>
      </div>

      <table className="schedule-table">
        <thead>
          <tr>
            <th>TANGGAL</th>
            <th>SHIFT</th>
            <th>JAM</th>
            <th>KARYAWAN BERTUGAS</th>
            <th style={{ textAlign: 'center' }}>JML</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(grouped).map(([date, rows]) => (
            <React.Fragment key={date}>
              <tr className="day-sep">
                <td colSpan={5}>
                  <span className="day-sep-label">{formatDateFull(date)}</span>
                </td>
              </tr>
              {rows.map((row, idx) => (
                <tr key={`${date}-${idx}`}>
                  <td className="td-date">{formatDate(row.date)}</td>
                  <td>
                    <span className={`shift-pill ${getShiftClass(row.shift)}`}>
                      {row.shift}
                    </span>
                  </td>
                  <td className="td-hours">{row.hours}</td>
                  <td>
                    <div className="td-employees">
                      {row.employees.length > 0 ? (
                        row.employees.map((e, i) => {
                          const isSelected =
                            selectedChip &&
                            selectedChip.date === row.date &&
                            selectedChip.empName === e;
                          return (
                            <span
                              key={i}
                              className={`emp-chip ${isSelected ? 'selected-chip' : ''}`}
                              onClick={() => handleChipClick(row.date, idx, e, i)}
                              style={{
                                cursor: onUpdateData ? 'pointer' : 'default',
                                background: isSelected ? 'var(--yellow)' : undefined,
                                color: isSelected ? '#000' : undefined,
                                fontWeight: isSelected ? 'bold' : 'normal',
                              }}
                            >
                              {e}
                            </span>
                          );
                        })
                      ) : (
                        <span style={{ color: 'var(--red)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                          KOSONG
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="td-count">{row.employee_count}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
